"use client"
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

/**
 * HOW IT WORKS (item 5): redesigned from four identical cards into a
 * connected timeline — a single line running through four numbered
 * stops, each revealing on scroll. Keeps the exact four-step copy from
 * ui-ux-spec.md's required "four-step journey".
 */

import { useI18n } from "@/i18n"
import { Reveal } from "./Reveal"

export function HowItWorks() {
  const { t } = useI18n()

  const steps = [
    { title: t("landingPage.howItWorks.step1Title"), body: t("landingPage.howItWorks.step1Body") },
    { title: t("landingPage.howItWorks.step2Title"), body: t("landingPage.howItWorks.step2Body") },
    { title: t("landingPage.howItWorks.step3Title"), body: t("landingPage.howItWorks.step3Body") },
    { title: t("landingPage.howItWorks.step4Title"), body: t("landingPage.howItWorks.step4Body") },
  ]

  const dotColors = ["bg-green", "bg-coral", "bg-gold", "bg-sky"]

  return (
    <section id="how" className="scroll-mt-[12vh] border-b border-line bg-paper">
      <div className="mx-auto max-w-6xl px-4 py-16 md:px-8">
        <Reveal>
          <p className="text-sm font-semibold tracking-wide text-green uppercase">
            {t("landingPage.howItWorks.eyebrow")}
          </p>
          <h2 className="mt-2 text-3xl font-bold text-ink md:text-4xl">
            {t("landingPage.howItWorks.heading")}
          </h2>
          <p className="mt-3 max-w-prose text-ink-2">{t("landingPage.howItWorks.lead")}</p>
        </Reveal>

        <ol className="relative mt-12 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          <div
            aria-hidden="true"
            className="absolute top-5 right-0 left-0 hidden h-px bg-line lg:block"
          />
          {steps.map((step, index) => (
            <Reveal
              key={step.title}
              as="li"
              index={index + 1}
              className="relative flex flex-col gap-3"
            >
              <span
                aria-hidden="true"
                className={`relative z-10 flex size-10 items-center justify-center rounded-bb-full text-sm font-black text-paper ${dotColors[index]}`}
              >
                {index + 1}
              </span>
              <h3 className="text-lg font-semibold text-ink">{step.title}</h3>
              <p className="text-sm text-ink-2">{step.body}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  )
}
